/**
 * CompanyBrainEditor — Admin → Company Brain.
 *
 * The operator's create/edit surface for the org's Brand Brain: voice &
 * tone, positioning, key offers, and free-form brand-markdown notes. Every
 * content / proposal / blog / email output composes its brand fragment
 * from these records (src/lib/brandBrain/compose.ts), so this form is
 * literally what makes generated output on-brand.
 *
 * All decisions are the PURE editor model (src/lib/brandBrain/editor.ts):
 * validateBrandInput gates the save, applyBrandFields upserts the four
 * deterministic entries, fieldsFromBrain reloads them. Persistence is
 * loadBrandBrain/saveBrandBrain riding the shipped db store — this file
 * is read → edit → validate → persist → toast, nothing clever.
 */

import { useEffect, useMemo, useState } from 'react';
import { BrainCircuit, Check, CircleDashed, Save } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { useToast } from './ToastProvider';
import { useAuth } from '../lib/auth/AuthProvider';
import { C } from '../lib/tokens';
import { emptyBrandBrain, type BrandBrain } from '../lib/brandBrain/schema';
import {
  BRAND_FIELDS,
  applyBrandFields,
  emptyBrandFieldValues,
  fieldsFromBrain,
  setBrandFieldKeys,
  validateBrandInput,
  type BrandFieldKey,
  type BrandFieldValues,
  type BrandInputIssue,
} from '../lib/brandBrain/editor';
import { loadBrandBrain, saveBrandBrain } from '../lib/brandBrain/persist';

export function CompanyBrainEditor() {
  const { user } = useAuth();
  const { push } = useToast();

  const [brain, setBrain] = useState<BrandBrain>(emptyBrandBrain);
  const [values, setValues] = useState<BrandFieldValues>(emptyBrandFieldValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [issues, setIssues] = useState<BrandInputIssue[]>([]);

  // Load-on-mount: the reload path. Saved fields come back verbatim.
  useEffect(() => {
    let active = true;
    loadBrandBrain()
      .then((loaded) => {
        if (!active) return;
        setBrain(loaded);
        setValues(fieldsFromBrain(loaded));
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const setKeys = useMemo(() => new Set(setBrandFieldKeys(brain)), [brain]);
  const issueFor = (key: BrandFieldKey | 'form') =>
    issues.find((i) => i.field === key)?.message;

  const onChange = (key: BrandFieldKey, text: string) => {
    setValues((v) => ({ ...v, [key]: text }));
    setDirty(true);
    if (issues.length > 0) setIssues([]);
  };

  const onSave = async () => {
    const check = validateBrandInput(values);
    if (!check.ok) {
      setIssues(check.issues);
      return;
    }
    const updatedBy = user?.displayName || user?.email || 'operator';
    const next = applyBrandFields(brain, values, {
      updatedBy,
      now: new Date().toISOString(),
    });
    setSaving(true);
    try {
      await saveBrandBrain(next, updatedBy);
      setBrain(next);
      setValues(fieldsFromBrain(next));
      setDirty(false);
      setIssues([]);
      const count = setBrandFieldKeys(next).length;
      push({
        kind: 'success',
        text: 'Brand Brain saved.',
        detail: `${count} of ${BRAND_FIELDS.length} brand fields set — every generated output now composes from them.`,
      });
    } catch (err) {
      push({
        kind: 'error',
        text: 'Could not save the Brand Brain.',
        detail: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,0,255,0.28)',
              color: C.magentaSoft,
              flexShrink: 0,
            }}
          >
            <BrainCircuit size={19} strokeWidth={1.75} />
          </span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h2 className="t-section-title" style={{ margin: 0 }}>Company Brain</h2>
            <p className="t-meta" style={{ margin: '3px 0 0' }}>
              The single source of truth on brand voice. Everything the AI writes — content,
              proposals, blogs, email — composes from these fields.
            </p>
          </div>
          {/* Coverage chips: which fields are set (persisted, not just typed). */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {BRAND_FIELDS.map((f) => {
              const isSet = setKeys.has(f.key);
              return (
                <span
                  key={f.key}
                  className="glass-pill"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 10px',
                    fontSize: 12,
                    color: isSet ? C.green : C.textMuted,
                    border: `1px solid ${isSet ? 'rgba(80,220,140,0.35)' : 'rgba(255,255,255,0.10)'}`,
                  }}
                >
                  {isSet ? <Check size={12} /> : <CircleDashed size={12} />}
                  {f.label}
                </span>
              );
            })}
          </div>
        </div>
      </GlassPanel>

      <GlassPanel animate={false} style={{ padding: '18px 20px' }}>
        {loading ? (
          <p className="t-meta" style={{ margin: 0 }}>Loading the Brand Brain…</p>
        ) : (
          <div style={{ display: 'grid', gap: 18 }}>
            {issueFor('form') && (
              <p role="alert" style={{ margin: 0, color: '#ff6b6b', fontSize: 13 }}>
                {issueFor('form')}
              </p>
            )}
            {BRAND_FIELDS.map((f) => {
              const fieldIssue = issueFor(f.key);
              const inputId = `company-brain-${f.key}`;
              return (
                <div key={f.key} style={{ display: 'grid', gap: 6 }}>
                  <label htmlFor={inputId} style={{ display: 'grid', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                      {f.label}
                    </span>
                    <span className="t-meta">{f.hint}</span>
                  </label>
                  <textarea
                    id={inputId}
                    value={values[f.key]}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    rows={f.key === 'notes' ? 8 : 4}
                    placeholder={setKeys.has(f.key) ? undefined : 'Not set yet — teach the AI here.'}
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${fieldIssue ? 'rgba(255,80,80,0.55)' : 'rgba(255,255,255,0.12)'}`,
                      background: 'rgba(255,255,255,0.04)',
                      color: C.text,
                      fontSize: 13,
                      lineHeight: 1.5,
                      fontFamily: f.key === 'notes' ? 'ui-monospace, monospace' : 'inherit',
                    }}
                  />
                  {fieldIssue && (
                    <span role="alert" style={{ color: '#ff6b6b', fontSize: 12 }}>
                      {fieldIssue}
                    </span>
                  )}
                </div>
              );
            })}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={() => void onSave()}
                disabled={saving || !dirty}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  opacity: saving || !dirty ? 0.6 : 1,
                }}
              >
                <Save size={14} />
                {saving ? 'Saving…' : 'Save Brand Brain'}
              </button>
              <span className="t-meta">
                {dirty
                  ? 'Unsaved changes.'
                  : setKeys.size > 0
                    ? `${setKeys.size} of ${BRAND_FIELDS.length} fields set · applies to all output surfaces.`
                    : 'Nothing set yet — outputs fall back to a neutral voice until you save.'}
              </span>
            </div>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}
